
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function ClaremontPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Claremont Painters - Professional Painting Services Cape Town";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Claremont historic and modern properties. Expert interior, exterior, and heritage restoration painting. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Claremont painters, heritage restoration, historic home painting, Victorian house painting, period property painting, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/claremont`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Claremont Painters - Professional Painting Services",
      "description": "Professional painting services for Claremont historic and modern properties. Expert interior, exterior, and heritage restoration painting.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/claremont`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Claremont",
        "description": "Heritage and modern painting services for Claremont's diverse architectural properties.",
        "areaServed": "Claremont, Cape Town",
        "serviceType": "Heritage and Modern Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Claremont')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-ancient-gate-line',
      title: 'Heritage Restoration',
      description: 'Specialized restoration painting for Claremont\'s historic Victorian and Edwardian properties using period-appropriate techniques and materials.',
      price: 'From R20,000',
      features: ['Period color matching', 'Traditional techniques', 'Heritage compliance', 'Architectural preservation']
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Victorian Home Painting',
      description: 'Expert painting services for Claremont\'s beautiful Victorian homes including detailed woodwork, ornate features, and authentic finishes.',
      price: 'From R15,000',
      features: ['Detailed woodwork', 'Ornate features', 'Authentic colors', 'Period accuracy']
    },
    {
      icon: 'ri-building-3-line',
      title: 'Modern Property Painting',
      description: 'Contemporary painting solutions for Claremont\'s newer developments and modern homes with current trends and durable finishes.',
      price: 'From R12,000',
      features: ['Modern techniques', 'Contemporary colors', 'Durable finishes', 'Trend awareness']
    },
    {
      icon: 'ri-brush-2-line',
      title: 'Interior Design Painting',
      description: 'Professional interior painting services that complement Claremont\'s diverse architectural styles from heritage to contemporary.',
      price: 'From R8,000',
      features: ['Style matching', 'Color consultation', 'Feature walls', 'Design coordination']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Exterior Protection',
      description: 'Comprehensive exterior painting and protection services for Claremont properties including weather resistance and maintenance.',
      price: 'From R14,000',
      features: ['Weather protection', 'UV resistance', 'Moisture barriers', 'Long-term durability']
    },
    {
      icon: 'ri-palette-fill',
      title: 'Color Consultation',
      description: 'Expert color consultation services for both heritage and modern properties in Claremont with historical accuracy and contemporary appeal.',
      price: 'From R2,500',
      features: ['Historical research', 'Period authenticity', 'Modern alternatives', 'Professional advice']
    }
  ];

  const testimonials = [
    {
      name: 'Margaret Williams',
      property: 'Victorian Family Home',
      rating: 5,
      text: 'Exceptional restoration work on our 1890s Victorian home. The team\'s knowledge of period colors and techniques brought our heritage property back to its original beauty.',
      image: 'https://public.readdy.ai/ai/img_res/0808044e21283a3cf394428a8c372311.jpg'
    },
    {
      name: 'James Robertson',
      property: 'Edwardian Cottage',
      rating: 5,
      text: 'Outstanding attention to historical detail and craftsmanship. Our Edwardian cottage looks magnificent and the authentic color scheme is perfect for the period.',
      image: 'https://readdy.ai/api/search-image?query=Distinguished%20gentleman%20homeowner%20in%20Edwardian%20cottage%20in%20Claremont%20with%20period%20architecture%2C%20heritage%20restoration%2C%20satisfied%20client&width=80&height=80&seq=claremont2&orientation=squarish'
    },
    {
      name: 'Emma Thompson',
      property: 'Modern Claremont Home',
      rating: 5,
      text: 'Professional service for our contemporary home. They balanced modern trends with the neighborhood\'s character perfectly. Excellent quality and attention to detail.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20woman%20homeowner%20in%20contemporary%20Claremont%20home%20with%20stylish%20interior%2C%20professionally%20painted%20walls%2C%20satisfied%20customer&width=80&height=80&seq=claremont3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Victorian Manor Restoration',
      description: 'Complete heritage restoration of a grand Victorian manor including authentic color schemes, detailed woodwork, and period-appropriate finishes',
      image: 'https://readdy.ai/api/search-image?query=Grand%20Victorian%20manor%20in%20Claremont%20with%20restored%20painted%20exterior%2C%20heritage%20architecture%2C%20period%20features%2C%20professional%20restoration%20work&width=400&height=300&seq=claremont4&orientation=landscape'
    },
    {
      title: 'Edwardian Cottage Revival',
      description: 'Authentic restoration painting of charming Edwardian cottage with traditional techniques and historically accurate color palette',
      image: 'https://readdy.ai/api/search-image?query=Charming%20Edwardian%20cottage%20in%20Claremont%20with%20restored%20painted%20exterior%2C%20period%20architecture%2C%20heritage%20colors%2C%20professional%20painting&width=400&height=300&seq=claremont5&orientation=landscape'
    },
    {
      title: 'Modern Family Home',
      description: 'Contemporary interior and exterior painting for modern Claremont home with stylish color schemes and durable finishes',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Claremont%20with%20contemporary%20painted%20exterior%2C%20stylish%20architecture%2C%20professional%20residential%20painting&width=400&height=300&seq=claremont6&orientation=landscape'
    },
    {
      title: 'Heritage Apartment Building',
      description: 'Comprehensive restoration of historic apartment building including common areas, individual units, and exterior heritage features',
      image: 'https://readdy.ai/api/search-image?query=Historic%20apartment%20building%20in%20Claremont%20with%20restored%20painted%20exterior%2C%20heritage%20architecture%2C%20period%20features%2C%20professional%20restoration&width=400&height=300&seq=claremont7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Victorian%20heritage%20home%20in%20Claremont%20with%20period%20architecture%2C%20painting%20equipment%2C%20scaffolding%2C%20historic%20neighborhood%20setting&width=1920&height=1080&seq=claremonthero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Claremont
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Claremont's heritage and modern properties. Specialized restoration for Victorian and Edwardian homes, plus contemporary solutions for modern developments with authentic period techniques and materials.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Claremont Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in Claremont
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Claremont is renowned for its rich architectural heritage, featuring some of Cape Town's most beautiful Victorian and Edwardian homes alongside modern developments. Our specialized painting services honor this diverse architectural landscape, providing both heritage restoration expertise and contemporary painting solutions.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located in Cape Town's southern suburbs, Claremont's tree-lined streets showcase a unique blend of historical charm and modern convenience. We understand the importance of preserving the area's heritage character while meeting the needs of contemporary homeowners who value both authenticity and modern living standards.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with Claremont's diverse property types, from grand Victorian manors to modern family homes, ensures we deliver results that respect the neighborhood's character while providing the durability and aesthetic appeal expected by discerning property owners.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Claremont%20neighborhood%20with%20Victorian%20and%20Edwardian%20homes%2C%20tree-lined%20streets%2C%20heritage%20architecture%2C%20mix%20of%20historic%20and%20modern%20properties&width=600&height=400&seq=claremont8&orientation=landscape"
                alt="Claremont Neighborhood"
                className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Claremont Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Claremont's heritage properties and modern developments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                {/* Updated icon container */}
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={
                    index === 0 ? "ri-home-4-line text-black text-2xl" :
                    index === 1 ? "ri-building-line text-black text-2xl" :
                    index === 2 ? "ri-community-line text-black text-2xl" :
                    `${service.icon} text-black text-2xl`
                  }></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Claremont Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in heritage restoration and modern painting with recent successful projects in Claremont.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Claremont Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Claremont property owners who have experienced our exceptional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Claremont Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's premier painting specialists for Claremont. Our expert team is ready to provide you with heritage restoration or modern painting solutions tailored to your property's unique character.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
